import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Sidebar from '../components/Sidebar';
import { authService } from '../services/authService';
import AdminApprovalModal from '../components/AdminApprovalModal';

const AdminUsers = () => {
  const [searchParams] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Account creation state
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [createAccountForm, setCreateAccountForm] = useState({
    email: '',
    displayName: '',
    role: 'owner',
    companyName: '',
    phoneNumber: '',
    address: ''
  });

  // Sync filters from URL (e.g. from dashboard "Pending Approvals" link)
  useEffect(() => {
    const role = searchParams.get('role');
    const status = searchParams.get('status');
    if (role) setFilterRole(role);
    if (status) setFilterStatus(status);
  }, [searchParams]);

  useEffect(() => {
    if (currentUser && userData) {
      loadUsers();
    }
  }, [currentUser, userData]);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, filterRole, filterStatus, sortBy, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('Loading users...');
      const allUsers = await authService.getAllUsers();
      console.log('Users loaded:', allUsers.length);
      
      // Log a sample user to check the structure
      if (allUsers.length > 0) {
        console.log('Sample user:', allUsers[0]);
        console.log('Sample user createdAt type:', typeof allUsers[0].createdAt);
        if (allUsers[0].createdAt) {
          console.log('Sample user createdAt value:', allUsers[0].createdAt);
          console.log('Sample user createdAt has toDate:', !!allUsers[0].createdAt.toDate);
        }
      }
      
      setUsers(allUsers);
      setLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      setLoading(false);
    }
  };

  const filterAndSortUsers = () => {
    let filtered = users.filter(user => {
      const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.uid?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
      return matchesSearch && matchesRole && matchesStatus;
    });

    // Sort users
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'createdAt') {
        // Handle Firebase Timestamp objects
        if (aValue && aValue.toDate && typeof aValue.toDate === 'function') {
          aValue = aValue.toDate();
        } else {
          aValue = new Date(aValue || 0);
        }
        if (bValue && bValue.toDate && typeof bValue.toDate === 'function') {
          bValue = bValue.toDate();
        } else {
          bValue = new Date(bValue || 0);
        }
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredUsers(filtered);
  };

  const handleUserAction = async (userId, action) => {
    try {
      switch (action) {
        case 'suspend':
          await authService.suspendUser(userId);
          break;
        case 'activate':
          await authService.activateUser(userId);
          break;
        case 'delete':
          // Show comprehensive deletion confirmation
          const userToDelete = users.find(u => u.uid === userId);
          if (!userToDelete) {
            alert('User not found');
            return;
          }
          
          const deleteConfirmation = window.confirm(
            `⚠️ COMPREHENSIVE USER DELETION ⚠️\n\n` +
            `Are you absolutely sure you want to delete user:\n` +
            `• Email: ${userToDelete.email}\n` +
            `• Name: ${userToDelete.displayName || 'N/A'}\n` +
            `• Role: ${userToDelete.role}\n\n` +
            `This will permanently delete:\n` +
            `• User account and profile\n` +
            `• All properties (if owner)\n` +
            `• All tickets and bids\n` +
            `• All payments and documents\n` +
            `• All chat messages and notifications\n` +
            `• All related data\n\n` +
            `This action CANNOT be undone!\n\n` +
            `Type "DELETE" to confirm:`
          );
          
          if (deleteConfirmation) {
            const userInput = prompt('Type "DELETE" to confirm permanent deletion:');
            if (userInput === 'DELETE') {
              setLoading(true);
              try {
                const result = await authService.deleteUser(userId);
                console.log('User deletion result:', result);
                alert(`✅ User deleted successfully!\n\n${result.message}\n\nNote: Firebase Auth account may still be active. Contact backend developer for complete removal.`);
              } catch (error) {
                console.error('User deletion failed:', error);
                alert(`❌ Failed to delete user: ${error.message}`);
              } finally {
                setLoading(false);
              }
            } else {
              alert('Deletion cancelled. User input did not match "DELETE".');
              return;
            }
          } else {
            alert('User deletion cancelled.');
            return;
          }
          break;
        case 'approve':
          // Find the user and open approval modal
          const user = users.find(u => u.uid === userId);
          if (user && user.role === 'serviceProvider') {
            setSelectedProvider(user);
            setShowApprovalModal(true);
          }
          return; // Don't reload users yet
        default:
          console.warn('Unknown action:', action);
          return;
      }
      
      // Reload users after action
      await loadUsers();
    } catch (error) {
      console.error('Error performing user action:', error);
      alert('Error performing action: ' + error.message);
    }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    try {
      const { email, displayName, role, companyName, phoneNumber, address } = createAccountForm;
      
      // Validate required fields based on role
      // Note: Phone number and address are no longer required for property owner accounts
      // Users will be prompted to provide this information upon login if missing
      
      if (role === 'serviceProvider' && !companyName.trim()) {
        alert('Company name is required for service provider accounts.');
        return;
      }
      
      // Default password for admin-created accounts
      const defaultPassword = 'property2025';
      
      // Create user data based on role
      const userData = {
        displayName,
        role,
        createdAt: new Date(),
        status: 'active',
        isAdminCreated: true
      };

      // Add role-specific fields and password change requirements
      if (role === 'serviceProvider') {
        userData.companyName = companyName;
        userData.phoneNumber = phoneNumber;
        userData.address = address;
        userData.requiresPasswordChange = true; // Keep for service providers
      } else if (role === 'owner') {
        userData.phoneNumber = phoneNumber;
        userData.address = address;
        // No password change required for property owners
      } else if (role === 'tenant') {
        userData.phoneNumber = phoneNumber;
        // No password change required for tenants
      }

      // Create the user account
      await authService.register(email, defaultPassword, userData);
      
      // Reset form and close modal
      setCreateAccountForm({
        email: '',
        displayName: '',
        role: 'owner',
        companyName: '',
        phoneNumber: '',
        address: ''
      });
      setShowCreateAccount(false);
      
      // Refresh user data
      await loadUsers();
      
      alert(`✅ Account created successfully for ${displayName}!\n\nEmail: ${email}\nPassword: ${defaultPassword}\n\nPlease send these credentials to the user.${role === 'owner' ? '\n\nNote: Property owners will be asked to complete their profile (phone & address) upon login if not provided during account creation.' : ''}${role === 'serviceProvider' ? '\n\nService providers will be prompted to change their password on first login.' : ''}`);
      
    } catch (error) {
      console.error('Error creating account:', error);
      alert(`❌ Failed to create account: ${error.message}`);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      alert('Please select users first');
      return;
    }

    let confirmMessage = '';
    if (action === 'delete') {
      confirmMessage = `⚠️ COMPREHENSIVE BULK USER DELETION ⚠️\n\n` +
        `Are you absolutely sure you want to delete ${selectedUsers.length} users?\n\n` +
        `This will permanently delete:\n` +
        `• All selected user accounts and profiles\n` +
        `• All their properties, tickets, bids\n` +
        `• All their payments, documents, and data\n` +
        `• All related information\n\n` +
        `This action CANNOT be undone!\n\n` +
        `Type "DELETE ALL" to confirm:`;
    } else {
      confirmMessage = `Are you sure you want to ${action} ${selectedUsers.length} users?`;
    }

    if (!window.confirm(confirmMessage)) return;

    // For delete action, require additional confirmation
    if (action === 'delete') {
      const userInput = prompt('Type "DELETE ALL" to confirm permanent deletion:');
      if (userInput !== 'DELETE ALL') {
        alert('Bulk deletion cancelled. User input did not match "DELETE ALL".');
        return;
      }
    }

    try {
      setLoading(true);
      for (const userId of selectedUsers) {
        await handleUserAction(userId, action);
      }
      setSelectedUsers([]);
      setShowBulkActions(false);
      alert(`✅ Successfully processed ${selectedUsers.length} users`);
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
      alert(`Failed to perform bulk ${action}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.uid));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'owner': return 'bg-blue-100 text-blue-800';
      case 'tenant': return 'bg-green-100 text-green-800';
      case 'serviceProvider': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApproval = async (result) => {
    // Reload users after approval/rejection
    await loadUsers();
    setShowApprovalModal(false);
    setSelectedProvider(null);
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
                <p className="text-sm text-gray-500 mt-0.5">Manage accounts, approve providers, create users</p>
              </div>
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </header>

          <div className="p-8">

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <input
                type="text"
                placeholder="Search users by email, name, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[300px]"
              />
              
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
                <option value="tenant">Tenant</option>
                <option value="serviceProvider">Service Provider</option>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateAccount(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Account
              </button>
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  showBulkActions 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Bulk Actions
              </button>
              <button
                onClick={loadUsers}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {showBulkActions && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {selectedUsers.length} user(s) selected
                  </span>
                  <button
                    onClick={toggleAllUsers}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkAction('activate')}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Activate All
                  </button>
                  <button
                    onClick={() => handleBulkAction('suspend')}
                    className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                  >
                    Suspend All
                  </button>
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Delete All
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={toggleAllUsers}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSortBy('role');
                      setSortOrder(sortBy === 'role' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    Role
                    {sortBy === 'role' && (
                      <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSortBy('status');
                      setSortOrder(sortBy === 'status' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    Status
                    {sortBy === 'status' && (
                      <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSortBy('createdAt');
                      setSortOrder(sortBy === 'createdAt' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    Created
                    {sortBy === 'createdAt' && (
                      <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentUsers.map((user) => (
                  <tr key={user.uid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.uid)}
                        onChange={() => toggleUserSelection(user.uid)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.displayName || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400">ID: {user.uid}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                          {user.status || 'active'}
                        </span>
                        {/* Show approval status for service providers */}
                        {user.role === 'serviceProvider' && (
                          <div className="text-xs">
                            {user.isApproved ? (
                              <span className="text-green-600">✓ Approved</span>
                            ) : user.isApproved === false ? (
                              <span className="text-red-600">✗ Rejected</span>
                            ) : (
                              <span className="text-yellow-600">⏳ Pending Approval</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        try {
                          if (!user.createdAt) return 'N/A';
                          if (user.createdAt.toDate && typeof user.createdAt.toDate === 'function') {
                            return user.createdAt.toDate().toLocaleDateString();
                          }
                          const date = new Date(user.createdAt);
                          if (isNaN(date.getTime())) return 'Invalid Date';
                          return date.toLocaleDateString();
                        } catch (error) {
                          console.warn('Error formatting date for user:', user.uid, error);
                          return 'Error';
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {/* Show approve button for pending service providers */}
                        {user.role === 'serviceProvider' && !user.isApproved && (
                          <button
                            onClick={() => handleUserAction(user.uid, 'approve')}
                            className="text-blue-600 hover:text-blue-900"
                            title="Review & Approve Provider"
                          >
                            Review
                          </button>
                        )}
                        
                        {user.status === 'active' ? (
                          <button
                            onClick={() => handleUserAction(user.uid, 'suspend')}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Suspend User"
                          >
                            Suspend
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUserAction(user.uid, 'activate')}
                            className="text-green-600 hover:text-green-900"
                            title="Activate User"
                          >
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => handleUserAction(user.uid, 'delete')}
                          className="text-red-600 hover:text-red-900"
                          title="Delete User"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstUser + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastUser, filteredUsers.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredUsers.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === number
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {users.filter(u => u.status === 'active').length}
              </p>
              <p className="text-sm text-gray-600">Active Users</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {users.filter(u => u.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-600">Pending Users</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                {users.filter(u => u.status === 'suspended').length}
              </p>
              <p className="text-sm text-gray-600">Suspended Users</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {users.filter(u => u.role === 'serviceProvider' && !u.isApproved).length}
              </p>
              <p className="text-sm text-gray-600">Pending Provider Approvals</p>
            </div>
          </div>
        </div>
          </div>

          {/* Create Account Modal */}
          {showCreateAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Create New Account</h2>
                <button
                  onClick={() => setShowCreateAccount(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Create accounts for users from your early access list. All accounts will use the default password "property2025" and users will be prompted to change it on first login.
              </p>
            </div>
            
            <div className="p-6">
              <form onSubmit={createAccount} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={createAccountForm.email}
                      onChange={(e) => setCreateAccountForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="user@example.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      id="displayName"
                      type="text"
                      required
                      value={createAccountForm.displayName}
                      onChange={(e) => setCreateAccountForm(prev => ({ ...prev, displayName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    User Role *
                  </label>
                  <select
                    id="role"
                    required
                    value={createAccountForm.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setCreateAccountForm(prev => ({ 
                        ...prev, 
                        role: newRole,
                        // Clear role-specific fields when switching roles
                        companyName: newRole === 'serviceProvider' ? prev.companyName : '',
                        phoneNumber: newRole === 'owner' ? prev.phoneNumber : '',
                        address: newRole === 'owner' ? prev.address : ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="owner">Property Owner</option>
                    <option value="tenant">Tenant</option>
                    <option value="serviceProvider">Service Provider</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                {/* Role-specific fields */}
                {createAccountForm.role === 'serviceProvider' && (
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      id="companyName"
                      type="text"
                      required
                      value={createAccountForm.companyName}
                      onChange={(e) => setCreateAccountForm(prev => ({ ...prev, companyName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ABC Services Ltd."
                    />
                  </div>
                )}

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      type="tel"
                      value={createAccountForm.phoneNumber}
                      onChange={(e) => setCreateAccountForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional - users can provide this information upon login</p>
                  </div>
                  
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      id="address"
                      type="text"
                      value={createAccountForm.address}
                      onChange={(e) => setCreateAccountForm(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123 Main St, City, State"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional - users can provide this information upon login</p>
                  </div>
                </div>

                {/* Password Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Default Password</p>
                      <p className="text-xs text-blue-600">All accounts will be created with the password: <strong>property2025</strong></p>
                      <p className="text-xs text-blue-600 mt-1">Users will be prompted to change their password on first login.</p>
                      {createAccountForm.role === 'owner' && (
                        <p className="text-xs text-blue-600 mt-1">Property owners will be asked to complete their profile (phone & address) upon login if not provided during account creation.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateAccount(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                  >
                    Create Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
          )}

          {/* Admin Approval Modal */}
          {showApprovalModal && selectedProvider && (
            <AdminApprovalModal
              isOpen={showApprovalModal}
              onClose={() => setShowApprovalModal(false)}
              provider={selectedProvider}
              onApproval={handleApproval}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
